import { Alert, FormGroup, Input, Modal, Button, Collapse } from "reactstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { Dispatch, SetStateAction, useState } from "react";

import CollapsibleGroup from "../../views/cloud/CreateCloudView/CreateCloudComponents/CollapsibleGroup";
import CreateModuleFromCSVDrawer from "../Drawers/CreateModuleFromCSVDrawer/CreateModuleFromCSVDrawer";
import IconCard from "../../views/workflows/IconCard";
import { newToastEvent } from "../../utils/ToastUtil";
import AppSpinner from "../../elements/AppSpinner";
import IconButton from "../../elements/IconButton";
import StrUtils from "../../utils/StrUtils";
import UppyFile from "../Inputs/UppyFile";

interface Props {
  open: boolean;
  toggle: () => void;
  onUpdate: (
    values: { Name: string; APIName: string },
    setValues: (data: any) => void,
    initialValues: { Name: string; APIName: string }
  ) => void;
  submitting: boolean;
  error: string | null;
  existingName?: string | null;
  mode: "create" | null;
  tenantID?: string;
  onClose: () => void;
  setQueryBuilder?: Dispatch<SetStateAction<boolean>>;
}

const initialValues = {
  Name: "",
  APIName: "",
};

type ActionState = "Selector" | "Custom";

const EditModuleModal = ({
  open,
  toggle,
  onUpdate,
  submitting,
  error,
  existingName,
  mode,
  tenantID = "",
  onClose = () => {},
  setQueryBuilder,
}: Props) => {
  const [values, setValues] = useState(initialValues);
  const [action, setAction] = useState<ActionState>("Selector");
  const [createFromCSVDrawer, setCreateDrawer] = useState(false);

  return (
    <>
      <Modal
        className="modal-dialog-centered"
        isOpen={open}
        toggle={handleClose}
      >
        <div className="modal-header">
          <h5 className="modal-title" id="exampleModalLabel">
            {existingName ? "Rename Data Source" : "Create A New Data Source"}
          </h5>
          <div className="d-flex justify-content-between">
            <IconButton
              onClick={handleClose}
              icon="close"
              tooltipText={""}
              style={{ padding: 0, margin: 0 }}
            />
          </div>
        </div>
        <div className="modal-body pt-0 pb-5">
          {action === "Selector" && setQueryBuilder && (
            <Selector
              setAction={setAction}
              handleClose={handleClose}
              setQueryBuilder={setQueryBuilder}
            />
          )}
          {(action === "Custom" || !setQueryBuilder) && (
            <Custom
              setValues={setValues}
              values={values}
              mode={mode}
              onUpdate={onUpdate}
              existingName={existingName || null}
              error={error}
              tenantID={tenantID}
              submitting={submitting}
              handleClose={handleClose}
              setCreateDrawer={setCreateDrawer}
            />
          )}
        </div>
      </Modal>
      <CreateModuleFromCSVDrawer
        open={createFromCSVDrawer}
        onClose={() => setCreateDrawer(false)}
      />
    </>
  );

  function handleClose() {
    toggle();
    onClose();
    setValues(initialValues);
    setAction("Selector");
  }
};

function Selector({
  setAction,
  handleClose,
  setQueryBuilder,
}: {
  setAction: Dispatch<SetStateAction<ActionState>>;
  handleClose: () => void;
  setQueryBuilder: Dispatch<SetStateAction<boolean>>;
}) {
  return (
    <>
      {/** @ts-ignore */}
      <IconCard
        icon="mdi mdi-database-outline"
        name="Custom Data Source"
        css="mb-3"
        onClick={() => setAction("Custom")}
        description={"Create a custom data source"}
        width="100%"
      />
      <hr className="my-0"></hr>
      {/** @ts-ignore */}
      <IconCard
        icon="mdi mdi-database-search-outline"
        name="Query Builder"
        onClick={() => {
          setQueryBuilder(true);
          handleClose();
        }}
        description={"Create data source from a custom query"}
      />
    </>
  );
}

function Custom({
  mode,
  onUpdate,
  values,
  existingName,
  setValues,
  handleClose,
  error,
  submitting,
  setCreateDrawer,
}: {
  mode: "create" | null;
  onUpdate: (
    values: { Name: string; APIName: string },
    setValues: (data: any) => void,
    initialValues: { Name: string; APIName: string }
  ) => void;
  values: { Name: string; APIName: string };
  setValues: Dispatch<SetStateAction<{ Name: string; APIName: string }>>;
  existingName: string | null;
  handleClose: () => void;
  error: string | null;
  tenantID: string;
  submitting: boolean;
  setCreateDrawer: Dispatch<SetStateAction<boolean>>;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const update = (e: any) => {
    e.preventDefault();
    onUpdate(values, setValues, initialValues);
  };

  const handleNameChange = (e: any) => {
    let value = e.target.value;

    let apiName = StrUtils.cleanAPIName(value);

    setValues({ Name: value, APIName: apiName });
  };

  const handleAPINameChange = (e: any) => {
    let value = e.target.value;

    value = StrUtils.cleanAPIName(value);

    setValues((prev) => ({ ...prev, APIName: value }));
  };

  return (
    <>
      <form onSubmit={update}>
        <div className="modal-body px-0 py-0">
          <Collapse isOpen>
            <FormGroup>
              <h5>Name</h5>
              <Input
                placeholder={existingName || "Name"}
                type="text"
                value={values?.Name}
                onChange={handleNameChange}
                autoFocus
              />
            </FormGroup>
            {!existingName && (
              <FormGroup>
                <h5>API Name</h5>
                <Input
                  placeholder="API Name"
                  type="text"
                  value={values?.APIName}
                  onChange={handleAPINameChange}
                />
              </FormGroup>
            )}
          </Collapse>

          {mode === "create" ? (
            <>
              <CollapsibleGroup
                collapsible={false}
                title="Create From CSV or Excel"
                size="sm"
                onClick={() => {
                  setCreateDrawer(true);
                  handleClose();
                }}
              />
              <CollapsibleGroup
                startOpen={false}
                title="Create From JSON"
                size="sm"
                containerClassName="tw:w-full tw:flex tw:justify-center"
              >
                <UppyFile
                  fileType="Text"
                  endpoint="/tenant/settings/crm/modules/file/import"
                  plugins={["GoogleDrive", "OneDrive"]}
                  customOnComplete={() => {
                    newToastEvent(
                      "success",
                      <div>
                        <p className="tw:m-0 tw:p-0">
                          {values.Name} Created Successfully
                        </p>
                        <small
                          onClick={() =>
                            navigate(`${currentPath}/manage/AllFields/fields`)
                          }
                          className="tw:underline tw:text-indigo-600 tw:decoration-transparent tw:hover:decoration-indigo-600 tw:cursor-pointer"
                        >
                          Click here to manage fields
                        </small>
                      </div>
                    );
                    handleClose();
                  }}
                  afterSubmit={() => {
                    setValues(initialValues);
                  }}
                />
              </CollapsibleGroup>
            </>
          ) : null}

          {error && (
            <Alert color="warning" className="mt-3 mb-0">
              {error}
            </Alert>
          )}
        </div>
        <div className="d-flex justify-content-end pt-3">
          <Button
            color="secondary"
            data-dismiss="modal"
            type="button"
            onClick={handleClose}
          >
            Close
          </Button>
          <Button
            color="primary"
            type="submit"
            disabled={submitting || !values.Name || !values.APIName}
            style={{ width: 100 }}
          >
            {submitting ? (
              <AppSpinner size="sm" color="white" />
            ) : (
              <>{existingName ? "Update" : "Create"}</>
            )}
          </Button>
        </div>
      </form>
    </>
  );
}

export default EditModuleModal;
